// Registry reads and commands on the cosmos API: query keys, query options and hooks shared by
// features. Every answer is parsed with the views of @cosmosai/api-client.
import type { CosmosClient } from "@cosmosai/api-client";
import {
  DeploymentView,
  DocumentView,
  EnvironmentView,
  EventView,
  formatRef,
  ObjectView,
  Outcome,
  ScopeView,
  type SpecRef,
  type State,
} from "@cosmosai/api-client";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useCosmos } from "./cosmos";
import { read } from "./read";

export interface SpecFilters {
  /** A kind slug, such as "agent". */
  kind?: string;
  project?: string;
  state?: State;
}

/** Keys under ["cosmos", workspace]: invalidating that prefix refreshes a whole workspace. */
export const cosmosKeys = {
  all: ["cosmos"] as const,
  // "*" is no workspace name (names are lowercase letters, digits and dashes).
  workspaces: () => ["cosmos", "*", "workspaces"] as const,
  workspace: (workspace: string) => ["cosmos", workspace] as const,
  teams: (workspace: string) => ["cosmos", workspace, "teams"] as const,
  projects: (workspace: string) => ["cosmos", workspace, "projects"] as const,
  specs: (workspace: string, filters: SpecFilters = {}) =>
    ["cosmos", workspace, "specs", filters] as const,
  spec: (workspace: string, ref: SpecRef) =>
    ["cosmos", workspace, "spec", formatRef(ref, null)] as const,
  version: (workspace: string, ref: SpecRef, version: number, format: "json" | "yaml") =>
    ["cosmos", workspace, "spec", formatRef(ref, null), version, format] as const,
  environments: (workspace: string) => ["cosmos", workspace, "environments"] as const,
  deployments: (workspace: string, env: string) =>
    ["cosmos", workspace, "environments", env, "deployments"] as const,
  history: (workspace: string, env: string) =>
    ["cosmos", workspace, "environments", env, "history"] as const,
};

const ws = (workspace: string) => ({ path: { workspace } });

export const workspacesQuery = (client: CosmosClient) =>
  queryOptions({
    queryKey: cosmosKeys.workspaces(),
    queryFn: () => read(z.array(ScopeView), "GET /workspaces", client.GET("/api/v1/workspaces")),
  });

export const teamsQuery = (client: CosmosClient, workspace: string) =>
  queryOptions({
    queryKey: cosmosKeys.teams(workspace),
    queryFn: () =>
      read(
        z.array(ScopeView),
        `GET /workspaces/${workspace}/teams`,
        client.GET("/api/v1/workspaces/{workspace}/teams", { params: ws(workspace) }),
      ),
  });

export const projectsQuery = (client: CosmosClient, workspace: string) =>
  queryOptions({
    queryKey: cosmosKeys.projects(workspace),
    queryFn: () =>
      read(
        z.array(ScopeView),
        `GET /workspaces/${workspace}/projects`,
        client.GET("/api/v1/workspaces/{workspace}/projects", { params: ws(workspace) }),
      ),
  });

export const specsQuery = (client: CosmosClient, workspace: string, filters: SpecFilters = {}) =>
  queryOptions({
    queryKey: cosmosKeys.specs(workspace, filters),
    queryFn: () =>
      read(
        z.array(ObjectView),
        `GET /workspaces/${workspace}/specs`,
        client.GET("/api/v1/workspaces/{workspace}/specs", {
          params: { path: { workspace }, query: filters },
        }),
      ),
  });

/** The spec endpoints have a project form for project-level kinds and a workspace form. */
function specPath(workspace: string, ref: SpecRef) {
  const base = { workspace, kind: ref.slug, name: ref.name };
  return ref.project
    ? ({ form: "project", path: { ...base, project: ref.project } } as const)
    : ({ form: "workspace", path: base } as const);
}

const endpointOf = (workspace: string, ref: SpecRef) =>
  ref.project
    ? `/workspaces/${workspace}/projects/${ref.project}/specs/${ref.slug}/${ref.name}`
    : `/workspaces/${workspace}/specs/${ref.slug}/${ref.name}`;

export const specQuery = (client: CosmosClient, workspace: string, ref: SpecRef) =>
  queryOptions({
    queryKey: cosmosKeys.spec(workspace, ref),
    queryFn: () => {
      const spec = specPath(workspace, ref);
      const call =
        spec.form === "project"
          ? client.GET("/api/v1/workspaces/{workspace}/projects/{project}/specs/{kind}/{name}", {
              params: { path: spec.path },
            })
          : client.GET("/api/v1/workspaces/{workspace}/specs/{kind}/{name}", {
              params: { path: spec.path },
            });
      return read(ObjectView, `GET ${endpointOf(workspace, ref)}`, call);
    },
  });

function versionCall(
  client: CosmosClient,
  workspace: string,
  ref: SpecRef,
  version: number,
  format: "json" | "yaml",
) {
  const spec = specPath(workspace, ref);
  const query = { format };
  const parseAs = format === "yaml" ? "text" : "json";
  return spec.form === "project"
    ? client.GET(
        "/api/v1/workspaces/{workspace}/projects/{project}/specs/{kind}/{name}/versions/{version}",
        { params: { path: { ...spec.path, version }, query }, parseAs },
      )
    : client.GET("/api/v1/workspaces/{workspace}/specs/{kind}/{name}/versions/{version}", {
        params: { path: { ...spec.path, version }, query },
        parseAs,
      });
}

/** One version as JSON (its document and source YAML). */
export const versionQuery = (
  client: CosmosClient,
  workspace: string,
  ref: SpecRef,
  version: number,
) =>
  queryOptions({
    queryKey: cosmosKeys.version(workspace, ref, version, "json"),
    queryFn: () =>
      read(
        DocumentView,
        `GET ${endpointOf(workspace, ref)}/versions/${version}`,
        versionCall(client, workspace, ref, version, "json"),
      ),
    // Versions never change once written; a discarded draft is a 404 on the next read.
    staleTime: Number.POSITIVE_INFINITY,
  });

/** One version's YAML file, as the platform stored it (`?format=yaml`). */
export const versionYamlQuery = (
  client: CosmosClient,
  workspace: string,
  ref: SpecRef,
  version: number,
) =>
  queryOptions({
    queryKey: cosmosKeys.version(workspace, ref, version, "yaml"),
    queryFn: () =>
      read(
        z.string(),
        `GET ${endpointOf(workspace, ref)}/versions/${version}?format=yaml`,
        versionCall(client, workspace, ref, version, "yaml"),
      ),
    staleTime: Number.POSITIVE_INFINITY,
  });

export const environmentsQuery = (client: CosmosClient, workspace: string) =>
  queryOptions({
    queryKey: cosmosKeys.environments(workspace),
    queryFn: () =>
      read(
        z.array(EnvironmentView),
        `GET /workspaces/${workspace}/environments`,
        client.GET("/api/v1/workspaces/{workspace}/environments", { params: ws(workspace) }),
      ),
  });

export const deploymentsQuery = (client: CosmosClient, workspace: string, env: string) =>
  queryOptions({
    queryKey: cosmosKeys.deployments(workspace, env),
    queryFn: () =>
      read(
        z.array(DeploymentView),
        `GET /workspaces/${workspace}/environments/${env}/deployments`,
        client.GET("/api/v1/workspaces/{workspace}/environments/{env}/deployments", {
          params: { path: { workspace, env } },
        }),
      ),
  });

export const historyQuery = (client: CosmosClient, workspace: string, env: string) =>
  queryOptions({
    queryKey: cosmosKeys.history(workspace, env),
    queryFn: () =>
      read(
        z.array(EventView),
        `GET /workspaces/${workspace}/environments/${env}/history`,
        client.GET("/api/v1/workspaces/{workspace}/environments/{env}/history", {
          params: { path: { workspace, env } },
        }),
      ),
  });

export const useWorkspaces = () => useQuery(workspacesQuery(useCosmos()));
export const useTeams = (workspace: string) => useQuery(teamsQuery(useCosmos(), workspace));
export const useProjects = (workspace: string) => useQuery(projectsQuery(useCosmos(), workspace));
export const useSpecs = (workspace: string, filters: SpecFilters = {}) =>
  useQuery(specsQuery(useCosmos(), workspace, filters));
export const useSpec = (workspace: string, ref: SpecRef) =>
  useQuery(specQuery(useCosmos(), workspace, ref));
export const useEnvironments = (workspace: string) =>
  useQuery(environmentsQuery(useCosmos(), workspace));
export const useDeployments = (workspace: string, env: string) =>
  useQuery(deploymentsQuery(useCosmos(), workspace, env));
export const useHistory = (workspace: string, env: string) =>
  useQuery(historyQuery(useCosmos(), workspace, env));

/** One version as JSON; `version` null waits. */
export function useVersion(workspace: string, ref: SpecRef, version: number | null) {
  const query = versionQuery(useCosmos(), workspace, ref, version ?? 0);
  return useQuery({ ...query, enabled: version !== null });
}

/** One version's YAML file; `version` null waits. */
export function useVersionYaml(workspace: string, ref: SpecRef, version: number | null) {
  const query = versionYamlQuery(useCosmos(), workspace, ref, version ?? 0);
  return useQuery({ ...query, enabled: version !== null });
}

/** A command's outcome; the workspace's cached reads are refreshed after it, unless it writes nothing. */
function useRegistryCommand<T>(
  workspace: string,
  endpoint: string,
  send: (client: CosmosClient, input: T) => Promise<{ data?: unknown }>,
  writes = true,
) {
  const client = useCosmos();
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (input: T) => read(Outcome, endpoint, send(client, input)),
    onSettled: () =>
      writes ? queries.invalidateQueries({ queryKey: cosmosKeys.workspace(workspace) }) : undefined,
  });
}

/** Freezes drafts: `refs` carry their version (`kind/project:name@N`). */
export const usePublish = (workspace: string) =>
  useRegistryCommand(
    workspace,
    `POST /workspaces/${workspace}/publish`,
    (client, body: { refs: string[]; project?: string | null }) =>
      client.POST("/api/v1/workspaces/{workspace}/publish", { params: ws(workspace), body }),
  );

/** Retires a published version (`ref` with its version). */
export const useDeprecate = (workspace: string) =>
  useRegistryCommand(
    workspace,
    `POST /workspaces/${workspace}/deprecate`,
    (client, body: { ref: string; project?: string | null }) =>
      client.POST("/api/v1/workspaces/{workspace}/deprecate", { params: ws(workspace), body }),
  );

/** Deletes a draft that runs nowhere but in development. */
export const useDiscard = (workspace: string) =>
  useRegistryCommand(
    workspace,
    `DELETE /workspaces/${workspace}/…/versions`,
    (client, { ref, version }: { ref: SpecRef; version: number }) => {
      const spec = specPath(workspace, ref);
      return spec.form === "project"
        ? client.DELETE(
            "/api/v1/workspaces/{workspace}/projects/{project}/specs/{kind}/{name}/versions/{version}",
            { params: { path: { ...spec.path, version } } },
          )
        : client.DELETE("/api/v1/workspaces/{workspace}/specs/{kind}/{name}/versions/{version}", {
            params: { path: { ...spec.path, version } },
          });
    },
  );

export const useCreateTeam = (workspace: string) =>
  useRegistryCommand(
    workspace,
    `POST /workspaces/${workspace}/teams`,
    (client, body: { name: string; displayName?: string | null }) =>
      client.POST("/api/v1/workspaces/{workspace}/teams", { params: ws(workspace), body }),
  );

/** A batch of YAML files for plan and apply, as the contract's `Batch`. */
export interface Batch {
  files: Array<{ path: string; text: string }>;
  environment: string | null;
  project: string | null;
}

/** The contract's limits of a batch (`Batch`, `File`): files, characters per file, path length. */
export const BATCH_LIMITS = { files: 1000, text: 1024 * 1024, path: 500 } as const;

/** Checks a batch and lists what apply would change; it writes nothing, so nothing is refreshed. */
export const usePlan = (workspace: string) =>
  useRegistryCommand(
    workspace,
    `POST /workspaces/${workspace}/plan`,
    (client, body: Batch) =>
      client.POST("/api/v1/workspaces/{workspace}/plan", { params: ws(workspace), body }),
    false,
  );

/** Writes a batch: new versions are drafts deployed to `environment`. */
export const useApply = (workspace: string) =>
  useRegistryCommand(workspace, `POST /workspaces/${workspace}/apply`, (client, body: Batch) =>
    client.POST("/api/v1/workspaces/{workspace}/apply", { params: ws(workspace), body }),
  );

/** Deploys versions running in `from` to `env`, the next tier; `refs` carry `@N`. */
export const usePromote = (workspace: string) =>
  useRegistryCommand(
    workspace,
    `POST /workspaces/${workspace}/environments/…/promote`,
    (
      client,
      { env, ...body }: { env: string; from: string; refs: string[]; project: string | null },
    ) =>
      client.POST("/api/v1/workspaces/{workspace}/environments/{env}/promote", {
        params: { path: { workspace, env } },
        body,
      }),
  );

/** Runs again in `env` a version it ran before; `ref` has no version. */
export const useRollback = (workspace: string) =>
  useRegistryCommand(
    workspace,
    `POST /workspaces/${workspace}/environments/…/rollback`,
    (client, { env, ...body }: { env: string; ref: string; to: number; project: string | null }) =>
      client.POST("/api/v1/workspaces/{workspace}/environments/{env}/rollback", {
        params: { path: { workspace, env } },
        body,
      }),
  );

export const useCreateProject = (workspace: string) =>
  useRegistryCommand(
    workspace,
    `POST /workspaces/${workspace}/projects`,
    (client, body: { name: string; team: string; displayName?: string | null }) =>
      client.POST("/api/v1/workspaces/{workspace}/projects", { params: ws(workspace), body }),
  );
